/**
 * Posição e aparência — fora de `props`.
 *
 * No modelo antigo isto vivia em `BaseProps`, misturado com `label`, `minLength`
 * e cores hex. Era o que obrigava o inspector a adivinhar, por heurística, qual
 * propriedade pertencia a qual grupo.
 */

/** Grade de 24 colunas, igual à do modelo antigo — telas existentes migram sem recálculo. */
export const GRID_COLUMNS = 24;

export type BreakpointName = 'sm' | 'md' | 'lg';

export interface LayoutSpec {
  /** Ordem entre os irmãos. Único eixo de posição em container stack/row/tabs. */
  order: number;

  /** Quantas das 24 colunas o nó ocupa. Ausente = largura cheia. */
  colSpan?: number;
  /** Quantas linhas da grade o nó ocupa. */
  rowSpan?: number;

  /**
   * Coordenada explícita. Só tem efeito em container `layoutMode: 'grid-free'`,
   * e o modo simples do editor nem expõe. Nos demais containers a posição vem
   * de `order` mais o empacotamento por linha.
   */
  col?: number;
  row?: number;

  /** Sobrescreve a largura por faixa de tela. Declarativo: nada é recalculado em runtime. */
  breakpoints?: Partial<Record<BreakpointName, { colSpan?: number }>>;
}

/**
 * Aparência por token semântico, não por cor.
 *
 * O módulo antigo tinha 34 propriedades de cor hex só em `TabsProps`, e o usuário
 * podia escolher qualquer valor — furando o tema pela própria mão. Aqui a escolha
 * é entre papéis; a cor sai do tema (`--tem-*`).
 */
export interface StyleTokens {
  /** Papel da superfície: fundo padrão, elevado, rebaixado ou de destaque. */
  surface?: 'default' | 'raised' | 'sunken' | 'accent';
  /** Peso do texto/conteúdo. */
  emphasis?: 'default' | 'muted' | 'strong';
  /** Intenção semântica, para faixas e realces. */
  intent?: 'neutral' | 'info' | 'success' | 'warning' | 'danger';

  /**
   * Escape hatch. As cores hex das telas antigas caem aqui na migração, para não
   * perder nada. O editor só expõe no modo avançado e avisa que quebra o tema.
   * Chave = propriedade CSS (ex.: 'background-color'), valor = literal.
   */
  raw?: Readonly<Record<string, string>>;
}

/** Como um container dispõe os filhos. Declarado no descriptor, não na tela. */
export type ContainerLayoutMode = 'grid' | 'grid-free' | 'row' | 'stack' | 'tabs';

export function defaultLayout(order = 0): LayoutSpec {
  return { order };
}
